// src/data/writtenDecisionReport.js
//
// T5 / D-23 (2026-10-16): THE WRITTEN REPORT OF A DECISION, WITHIN 30 DAYS, TO THE LOCAL CHAIRPERSON AND THE LOCAL'S
// SECRETARY. SMART constitution (2024), Article 21B, Section 44, read at source
// (data\agreements\smart-constitution-2024.source.txt lines 7561-7564):
//   "Decisions reached disposing of or settling claims, complaints, and grievances referred to herein shall be furnished
//    in writing, within thirty (30) days after such decision, to the Local Chairperson and Secretary of the Local
//    submitting such claims, complaints, and grievances."
// On a discipline case the decision disposing of or settling it is the case being RESOLVED (resolvedAt, written only by
// the disciplineAct function) - not the carrier's discipline decision, which is what the grievance is about. A case
// reopened and resolved again owes a report of the new resolution.
//
// The platform holds no Local Secretary of record (officeSuccession.js designates a Secretary for general_committee
// offices only), so it cannot send the report itself. What it does: the clock (resolved on as day one of WRITTEN_DAYS, the same
// arithmetic as progressionDecision.js datesFor, so the claim side and this side say the same date for the same day), and
// a dated record of each furnishing - to whom by office and name, how, and the document - so the case shows whether the
// duty was met. The record is appended to the case's writtenReports[] by an officer; never rewritten.
//
// PURE. No React, no Firestore.
import { WRITTEN_DAYS, etToday } from './progressionDecision.js';
import { lastDayOf } from './timeLimit';   // ruling 314 (forensic audit 2026-10-16)

export const RECIPIENTS = Object.freeze({
  local_chair: 'the Local Chairperson',
  local_secretary: 'the Secretary of the Local',
});
export const METHODS = Object.freeze({
  certified_mail: 'certified mail',
  mail: 'first-class mail',
  email: 'e-mail',
  hand: 'hand delivery',
});
export const SECTION_44_WORDS = 'Decisions reached disposing of or settling claims, complaints, and grievances referred to herein shall be '
  + 'furnished in writing, within thirty (30) days after such decision, to the Local Chairperson and Secretary of the Local '
  + 'submitting such claims, complaints, and grievances.';
export const SECTION_44_CITE = 'SMART constitution, Article 21B, Section 44';

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const s = (v) => (v == null ? '' : String(v)).trim();
function toMs(t) {
  if (t == null) return null;
  if (typeof t === 'number') return Number.isFinite(t) ? t : null;
  if (t instanceof Date) return t.getTime();
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t === 'object' && typeof t.seconds === 'number') return t.seconds * 1000;
  const ms = Date.parse(String(t));
  return Number.isNaN(ms) ? null : ms;
}

// The day the case was disposed of (ET), or null while it is open.
export function disposedOn(disciplineCase) {
  const c = disciplineCase || {};
  if (c.status !== 'resolved') return null;
  const ms = toMs(c.resolvedAt);
  return ms == null ? null : etToday(ms);
}

// One furnishing, validated. input: { to, toName, on, method, documentName, today }.
export function planWrittenReport(input, disciplineCase) {
  const i = input || {};
  const errors = [];
  const decided = disposedOn(disciplineCase);
  if (!decided) errors.push('The case is not resolved; there is no decision disposing of it to report yet.');
  if (!RECIPIENTS[i.to]) errors.push('Say who it was furnished to: the Local Chairperson or the Secretary of the Local.');
  if (!s(i.toName)) errors.push('Give the name of the person it was furnished to.');
  if (!ISO.test(s(i.on))) errors.push('The date it was furnished (YYYY-MM-DD) is required.');
  else if (i.on > (i.today || etToday())) errors.push('A report dated in the future has not been furnished yet.');
  else if (decided && i.on < decided) errors.push('It cannot have been furnished before the decision (' + decided + ').');
  if (!METHODS[i.method]) errors.push('Say how it was furnished: ' + Object.values(METHODS).join(', ') + '.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, errors: [], record: {
    to: i.to, toName: s(i.toName), on: i.on, method: i.method,
    documentName: s(i.documentName) || null, forDecisionOn: decided,
  } };
}

// Where the duty stands. reports = the case's writtenReports[]; only those furnished for THIS resolution count.
export function writtenReportStatus(disciplineCase, today) {
  const decided = disposedOn(disciplineCase);
  if (!decided) return { applies: false, line: null, rows: [] };
  // Forensic audit 2026-10-16: the day of the resolution is day one (ruling 314), so the 30th day is the last -
  // addDays(decided, 30) put it one day late. Same arithmetic as progressionDecision.datesFor, as the header promises.
  const dueOn = lastDayOf(decided, WRITTEN_DAYS);
  const t = today || etToday();
  const rows = (Array.isArray((disciplineCase || {}).writtenReports) ? disciplineCase.writtenReports : [])
    .filter(r => r && r.forDecisionOn === decided)
    .slice().sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0));
  const first = (to) => rows.find(r => r.to === to) || null;
  const lc = first('local_chair'), sec = first('local_secretary');
  const owed = [!lc && RECIPIENTS.local_chair, !sec && RECIPIENTS.local_secretary].filter(Boolean);
  const late = rows.filter(r => r.on > dueOn).map(r => RECIPIENTS[r.to]);
  let line;
  if (!owed.length) {
    line = 'The written decision was furnished to both offices' + (late.length ? ' - to ' + late.join(' and ') + ' AFTER ' + dueOn + ', late' : ', on time') + '.';
  } else {
    const left = Math.round((Date.parse(dueOn + 'T00:00:00Z') - Date.parse(t + 'T00:00:00Z')) / 86400000);
    line = 'The written decision is due by ' + dueOn + ' to ' + owed.join(' and ') + ' - '
      + (left < 0 ? Math.abs(left) + ' day' + (Math.abs(left) === 1 ? '' : 's') + ' OVERDUE' : left + ' day' + (left === 1 ? '' : 's') + ' left') + '.';
  }
  return { applies: true, decidedOn: decided, dueOn, rows, owed, late, overdue: owed.length > 0 && t > dueOn, line };
}
